import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RAMSALAB_ORG } from "../../constants/constants";
import {
  addWordMappingsApi,
  exportProjectMappingsApi,
  fetchDictionaryVersionsApi,
  fetchJobProgressApi,
  fetchJobSegmentsApi,
  fetchJobWordsApi,
  fetchProjectProgressApi,
  importDictionaryApi,
  removeWordMappingApi,
  searchDictionaryApi,
  addTempEntryApi,
  syncJobWordsApi,
  updateSegmentPhoneticsApi,
} from "../services/vocabMap.services";

export const VOCAB_VERSIONS_KEY = "vocab-versions";
export const VOCAB_SEARCH_KEY = "vocab-search";
export const VOCAB_JOB_WORDS_KEY = "vocab-job-words";
export const VOCAB_JOB_SEGMENTS_KEY = "vocab-job-segments";
export const VOCAB_JOB_PROGRESS_KEY = "vocab-job-progress";
export const VOCAB_PROJECT_PROGRESS_KEY = "vocab-project-progress";

const withOrg = (apiParams = {}) => {
  const params = { ...apiParams };
  params.org = localStorage.getItem(RAMSALAB_ORG);
  return params;
};

export const useDictionaryVersions = ({ queryConfig = {} } = {}) => {
  const { queryKey = [], ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_VERSIONS_KEY, ...queryKey],
    queryFn: () => fetchDictionaryVersionsApi(),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useDictionarySearch = ({ queryConfig }) => {
  const { queryKey, apiParams, ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_SEARCH_KEY, ...queryKey],
    queryFn: () => searchDictionaryApi(withOrg(apiParams)),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useJobSegments = ({ queryConfig }) => {
  const { queryKey, apiParams, ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_JOB_SEGMENTS_KEY, ...queryKey],
    queryFn: () => fetchJobSegmentsApi(withOrg(apiParams)),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useJobWords = ({ queryConfig }) => {
  const { queryKey, apiParams, ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_JOB_WORDS_KEY, ...queryKey],
    queryFn: () => fetchJobWordsApi(withOrg(apiParams)),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useJobProgress = ({ queryConfig }) => {
  const { queryKey, apiParams, ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_JOB_PROGRESS_KEY, ...queryKey],
    queryFn: () => fetchJobProgressApi(withOrg(apiParams)),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useProjectProgress = ({ queryConfig }) => {
  const { queryKey, apiParams, ...restConfig } = queryConfig;
  return useQuery({
    queryKey: [VOCAB_PROJECT_PROGRESS_KEY, ...queryKey],
    queryFn: () => fetchProjectProgressApi(withOrg(apiParams)),
    refetchOnWindowFocus: false,
    ...restConfig,
  });
};

export const useSyncJobWordsMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => syncJobWordsApi(withOrg(params)),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_WORDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_SEGMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_PROGRESS_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useAddMappingsMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => addWordMappingsApi(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_WORDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_SEGMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_PROGRESS_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useRemoveMappingMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => removeWordMappingApi(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_WORDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_SEGMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_PROGRESS_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useUpdateSegmentPhoneticsMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => updateSegmentPhoneticsApi(withOrg(params)),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_WORDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_SEGMENTS_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useAddTempEntryMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => addTempEntryApi(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_WORDS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_SEGMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_JOB_PROGRESS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VOCAB_SEARCH_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useImportDictionaryMutation = ({ mutationConfig } = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => importDictionaryApi(params),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [VOCAB_VERSIONS_KEY] });
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};

export const useExportProjectMappingsMutation = ({ mutationConfig } = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (params) => exportProjectMappingsApi(withOrg(params)),
    onSuccess,
    ...restConfig,
  });
};
